import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { fetchPartnerVoiceCallAggregateSummary, fetchVoiceCallAggregates } from './voice-calls-api';
import { daysToDateRange } from './voice-utils';

const PAGE_SIZE = 25;

export function useVoiceTenantCallAggregates(selectedDays: number = 0) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});

  const [prevSelectedDays, setPrevSelectedDays] = useState(selectedDays);
  if (selectedDays !== prevSelectedDays) {
    setPrevSelectedDays(selectedDays);
    setCurrentPage(1);
    setPageCursors({});
  }

  const cursor = pageCursors[currentPage];
  const dateRange = daysToDateRange(selectedDays);

  const aggregatesQuery = useQuery({
    queryKey: ['voiceCallAggregates', selectedDays, currentPage, cursor],
    queryFn: () =>
      fetchVoiceCallAggregates({
        cursor,
        limit: PAGE_SIZE,
        ...dateRange,
      }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const summaryQuery = useQuery({
    queryKey: ['voiceCallAggregateSummary', selectedDays, dateRange.startDate, dateRange.endDate],
    queryFn: () => fetchPartnerVoiceCallAggregateSummary(dateRange),
    staleTime: 60_000,
  });

  const goToNextPage = useCallback(() => {
    if (aggregatesQuery.data?.hasNextPage && aggregatesQuery.data.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]: aggregatesQuery.data!.cursor,
      }));
      setCurrentPage(p => p + 1);
    }
  }, [aggregatesQuery.data, currentPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(p => p - 1);
    }
  }, [currentPage]);

  return {
    data: aggregatesQuery.data?.data ?? [],
    summary: summaryQuery.data,
    isLoading: aggregatesQuery.isLoading,
    isFetching: aggregatesQuery.isFetching || summaryQuery.isFetching,
    summaryIsLoading: summaryQuery.isLoading,
    summaryError: summaryQuery.error,
    error: aggregatesQuery.error,
    hasNextPage: aggregatesQuery.data?.hasNextPage ?? false,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  };
}
